/**
 * SearchableVehicleSelect - Sökbar fordonsväljare med server-side sökning
 * Hämtar fordon från API:et med debounced sökning för att hantera tusentals fordon.
 * Kan även ta emot en statisk lista via vehicles-propen (bakåtkompatibelt).
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import { MagnifyingGlassIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TruckIcon } from '@heroicons/react/24/solid';
import { vehiclesService } from '@/services/vehicles/vehiclesService';

export interface VehicleOption {
  id: string;
  registrationNumber: string;
  brand?: string;
  model?: string;
  imei?: string;
}

interface SearchableVehicleSelectProps {
  /** Statisk fordonslista (valfri – om ej angiven hämtas från API) */
  vehicles?: VehicleOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  /** Filtrera fordon till vald organisation (och underorganisationer via API includeSubOrgs) */
  organizationId?: string;
}

function mapApiVehicle(v: any): VehicleOption {
  return {
    id: (v.id || v.vehicle_id)?.toString() || '',
    registrationNumber: v.registration_number || v.registrationNumber || '',
    brand: v.brand || v.make || '',
    model: v.model || '',
    imei: v.device_imei || v.imei || '',
  };
}

const DEBOUNCE_MS = 300;

const SearchableVehicleSelect: React.FC<SearchableVehicleSelectProps> = ({
  vehicles: staticVehicles,
  selectedValue,
  onChange,
  placeholder,
  disabled = false,
  loading: externalLoading = false,
  className = '',
  organizationId,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('copy.ui.searchOrSelectVehicle');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [apiVehicles, setApiVehicles] = useState<VehicleOption[]>([]);
  const [initialVehicles, setInitialVehicles] = useState<VehicleOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedVehicleInfo, setSelectedVehicleInfo] = useState<VehicleOption | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const useApi = !staticVehicles;

  // Ladda initiala fordon (första 50) vid mount / när organisation ändras
  useEffect(() => {
    if (!useApi) return;
    let cancelled = false;
    setSearchTerm('');
    setApiVehicles([]);
    const load = async () => {
      try {
        const result = await vehiclesService.getVehicles({
          limit: 50,
          includeSubOrgs: true,
          ...(organizationId ? { organizationId } : {}),
        });
        if (!cancelled) {
          setInitialVehicles(result.vehicles.map(mapApiVehicle));
        }
      } catch {
        if (!cancelled) setInitialVehicles([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [useApi, organizationId]);

  // Om vi har ett valt fordon men det inte finns i listan, hämta det
  useEffect(() => {
    if (!selectedValue || !useApi) return;
    const allKnown = [...initialVehicles, ...apiVehicles];
    const found = allKnown.find(v => v.id === selectedValue);
    if (found) {
      setSelectedVehicleInfo(found);
      return;
    }
    let cancelled = false;
    const fetchSelected = async () => {
      try {
        const v = await vehiclesService.getVehicleById(selectedValue);
        if (!cancelled && v) {
          setSelectedVehicleInfo(mapApiVehicle(v));
        }
      } catch {
        // Ignore
      }
    };
    fetchSelected();
    return () => { cancelled = true; };
  }, [selectedValue, useApi, initialVehicles, apiVehicles]);

  // Hitta det valda fordonet (statisk lista eller API)
  const selectedVehicle = useMemo(() => {
    if (!selectedValue) return null;
    if (staticVehicles) {
      return staticVehicles.find(v => v.id?.toString() === selectedValue) || null;
    }
    const allKnown = [...initialVehicles, ...apiVehicles];
    return allKnown.find(v => v.id === selectedValue) || selectedVehicleInfo;
  }, [staticVehicles, selectedValue, initialVehicles, apiVehicles, selectedVehicleInfo]);

  // Visa-lista: antingen sökresultat (API) eller statisk filtrering
  const displayVehicles = useMemo(() => {
    if (staticVehicles) {
      if (!searchTerm.trim()) return staticVehicles;
      const term = searchTerm.toLowerCase().trim();
      return staticVehicles.filter(v =>
        (v.registrationNumber || '').toLowerCase().includes(term) ||
        (v.brand || '').toLowerCase().includes(term) ||
        (v.model || '').toLowerCase().includes(term) ||
        (v.imei || '').toLowerCase().includes(term)
      );
    }
    if (searchTerm.trim()) return apiVehicles;
    return initialVehicles;
  }, [staticVehicles, searchTerm, apiVehicles, initialVehicles]);

  // Server-side sökning med debounce
  const doSearch = useCallback(async (term: string) => {
    if (!useApi) return;
    if (!term.trim()) {
      setApiVehicles([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const results = await vehiclesService.searchVehicles(term, organizationId);
      setApiVehicles(results.map(mapApiVehicle));
    } catch {
      setApiVehicles([]);
    } finally {
      setSearching(false);
    }
  }, [useApi, organizationId]);

  // Stäng dropdown när man klickar utanför
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setApiVehicles([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fokusera input när dropdown öppnas
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (vehicleId: string) => {
    onChange(vehicleId);
    setIsOpen(false);
    setSearchTerm('');
    setApiVehicles([]);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setApiVehicles([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (!isOpen) setIsOpen(true);

    if (useApi) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (val.trim().length >= 2) {
        setSearching(true);
        debounceRef.current = setTimeout(() => doSearch(val), DEBOUNCE_MS);
      } else {
        setApiVehicles([]);
        setSearching(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setApiVehicles([]);
    } else if (e.key === 'Enter' && displayVehicles.length === 1) {
      handleSelect(displayVehicles[0].id.toString());
    }
  };

  const isLoading = externalLoading || searching;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button / Display */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-3 py-2 
          border border-border rounded-md shadow-sm
          bg-bgElevated
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary'}
          ${isOpen ? 'ring-2 ring-primary border-primary' : ''}
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TruckIcon className="w-5 h-5 text-textTertiary flex-shrink-0" />
          {selectedVehicle ? (
            <span className="text-textDefault truncate">
              {selectedVehicle.registrationNumber}
              {selectedVehicle.brand && ` - ${selectedVehicle.brand}`}
              {selectedVehicle.model && ` ${selectedVehicle.model}`}
            </span>
          ) : (
            <span className="text-textSecondary">{resolvedPlaceholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selectedValue && !disabled && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-bgBase rounded"
            >
              <XMarkIcon className="w-4 h-4 text-textSecondary" />
            </button>
          )}
          <ChevronDownIcon className={`w-5 h-5 text-textSecondary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-bgElevated border border-border rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Sökfält */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textSecondary" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={useApi ? t('copy.ui.typeTwoCharsToSearch') : t('copy.ui.searchRegNumber')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md 
                         bg-bgBase text-textDefault placeholder-textSecondary
                         focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              )}
            </div>
          </div>

          {/* Lista med fordon */}
          <div className="overflow-y-auto max-h-60">
            {isLoading && displayVehicles.length === 0 ? (
              <div className="p-4 text-center text-textSecondary">
                <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                {t('copy.ui.searchingVehicles')}
              </div>
            ) : useApi && searchTerm.trim().length > 0 && searchTerm.trim().length < 2 ? (
              <div className="p-4 text-center text-textSecondary text-sm">
                {t('copy.ui.typeTwoCharsToSearch')}
              </div>
            ) : displayVehicles.length === 0 ? (
              <div className="p-4 text-center text-textSecondary">
                {searchTerm ? t('copy.ui.noVehiclesMatch', { term: searchTerm }) : t('copy.ui.noVehiclesAvailable')}
              </div>
            ) : (
              <>
                {/* Tom option */}
                {!searchTerm.trim() && (
                  <div
                    onClick={() => handleSelect('')}
                    className={`
                      px-3 py-2 cursor-pointer flex items-center gap-2
                      hover:bg-bgBase
                      ${!selectedValue ? 'bg-primary/10' : ''}
                    `}
                  >
                    <span className="text-textSecondary text-sm">{t('copy.ui.selectVehicleDash')}</span>
                  </div>
                )}
                
                {/* Fordons-lista */}
                {displayVehicles.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    onClick={() => handleSelect(vehicle.id.toString())}
                    className={`
                      px-3 py-2 cursor-pointer flex items-center gap-2
                      hover:bg-bgBase
                      ${selectedValue === vehicle.id?.toString() ? 'bg-primary/10 text-primary' : ''}
                    `}
                  >
                    <TruckIcon className="w-4 h-4 text-textTertiary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-textDefault truncate">
                        {vehicle.registrationNumber}
                      </div>
                      {(vehicle.brand || vehicle.model) && (
                        <div className="text-xs text-textSecondary truncate">
                          {[vehicle.brand, vehicle.model].filter(Boolean).join(' ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {useApi && searchTerm.trim() && displayVehicles.length >= 50 && (
                  <div className="px-3 py-2 text-xs text-textTertiary text-center border-t border-border">
                    {t('copy.ui.max50Hits')}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableVehicleSelect;
